// src/admin/components/IntegrationHealthCard.jsx
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import DashboardFeatureCard from './cards/DashboardFeatureCard';
import StatusPill from '../../shared/components/pill/StatusPill';
import { healthState, syncState } from './integrations/integrationStates';
import { getIntegrationManifest } from '../../integrations';
import { useCurrentUser } from '../auth/CurrentUserContext';
import { can } from '../auth/permissions';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { API_URL } from '../../config/env';

/**
 * What state to show depends on what the integration does.
 *
 * A ticketing integration has a health check — can it authenticate, can it still see its
 * projects — because its failures are silent. An IoT integration's meaningful state is whether
 * its last asset sync worked. Neither measure applies to the other, so the badge is chosen by
 * role rather than showing one column that is wrong for half the rows.
 */
function stateFor(integration) {
  const roles = integration.roles || [];

  if (roles.includes('upstream_ticketing')) {
    return healthState(integration.health_status);
  }

  if (roles.includes('iot_backend')) {
    // Same tones as the sync log and the integrations table; the wording is this card's own,
    // because here the state is read as a sentence about one integration.
    const labels = { success: 'Synced', partial: 'Partial sync', failed: 'Sync failed' };
    const state = syncState(integration.last_sync_status);
    return { ...state, label: labels[integration.last_sync_status] || state.label };
  }

  return { label: 'Active', tone: 'neutral' };
}

/** Anything an admin should look at, as opposed to anything merely not-green. */
function needsAttention(integration) {
  return ['degraded', 'failed'].includes(integration.health_status)
    || ['partial', 'failed'].includes(integration.last_sync_status);
}

/**
 * Compact dashboard view of the organization's active integrations and their state.
 *
 * Admin-only. The gate mirrors the backend: /v1/admin/integrations is behind
 * `integrations_manage`, which is admin-only, so a non-admin would only get a 403. Checking the
 * permission first avoids making that pointless request at all.
 */
export default function IntegrationHealthCard({ colSize = 'col-lg-6 col-md-12 mb-3' }) {
  const { user } = useCurrentUser();
  const allowed = can(user, 'integrations.manage');
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!allowed) return undefined;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetchWithAuth(`${API_URL}/v1/admin/integrations`, { cache: 'no-store' });
        if (!res.ok) throw new Error('Could not load integrations');
        const json = await res.json();
        // Disabled integrations are deliberately left out: this card answers "is what we rely on
        // working", not "what exists".
        const active = (json.enabled || []).filter((row) => row.status === 'active');
        if (!cancelled) setIntegrations(active);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load integrations');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [allowed]);

  if (!allowed) return null;
  // Nothing to report and nothing an admin need act on, so the card stays out of the way.
  if (!loading && !error && integrations.length === 0) return null;

  const attention = integrations.filter(needsAttention).length;

  return (
    <div className={colSize}>
      <DashboardFeatureCard
        title="Integrations"
        titleAside={attention > 0 ? (
          <StatusPill tone="warning">
            {`${attention} need${attention === 1 ? 's' : ''} attention`}
          </StatusPill>
        ) : null}
        footer={<Link to="/admin/organization/integrations" className="small">Manage integrations</Link>}
      >
        {loading && <div className="text-muted small">Loading integrations…</div>}
        {error && <div className="text-danger small">{error}</div>}

        <ul className="list-unstyled mb-0">
          {integrations.map((integration) => {
            const manifest = getIntegrationManifest(integration.type);
            const state = stateFor(integration);
            return (
              <li
                key={integration.id}
                className="d-flex align-items-center justify-content-between gap-2 py-2 border-bottom"
              >
                <div className="d-flex align-items-center gap-2 min-w-0">
                  {manifest?.logo && (
                    <img
                      src={manifest.logo}
                      alt=""
                      style={{ width: 20, height: 20 }}
                      className="flex-shrink-0"
                    />
                  )}
                  <span className="text-truncate">{integration.display_name}</span>
                </div>
                <StatusPill tone={state.tone} icon={state.icon} className="flex-shrink-0">
                  {state.label}
                </StatusPill>
              </li>
            );
          })}
        </ul>
      </DashboardFeatureCard>
    </div>
  );
}

IntegrationHealthCard.propTypes = {
  colSize: PropTypes.string,
};
